import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { type FixedSensorPort, type OpenSensorPort } from "../../Configurations/portConfigs.js";
import {
    findSensorClusterConfigKeyForLegacySensorType,
    sensorClusterConfigs,
} from "../../Configurations/SensorConfigurations/sensorClusters/sensorClusterConfigs.js";
import { type FirstPartyDeviceType } from "../../shared.js";
import { TimeOverride } from "../../utils/timeOverrideUtils.js";
import { DeviceVersion } from "../commonSubSchemas/deviceVersion.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { SensorClusterAddress } from "../commonSubSchemas/sensorClusterAddress.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { LoraNetworkProfile } from "../loraNetworkProfile/loraNetworkProfile.js";
import { LORA_MODE } from "../loraNetworkProfile/subSchemas/responsivenessSettings.js";
import {
    SensorCluster,
    type SensorClusterConfigKey,
    type SensorClusterOrigin,
} from "../sensorCluster/sensorCluster.js";
import { DEVICE_COLLECTION_NAME, type Device } from "./device.js";
import {
    GEN4_V14_PLUS_NETWORK_TEST_DURATION_MS,
    GEN4_V14_PLUS_NETWORK_TEST_VERSION_SELECTOR,
    NETWORK_TEST_DURATION_MS,
} from "./gen4NetworkTestConstants.js";
import { LoraNetworkProfileConfig } from "./subSchemas/loraNetworkProfileConfig.js";
import { ClassBSupportStatus } from "./types.js";
import { getFixedPortConnectedZoneIDs, getSensorsConnectedZoneIDs } from "./utilities.js";

export { getDisplayId } from "../commonGetters/getDisplayId.js";
export { getDisplayName } from "../commonGetters/getDisplayName.js";

/**
 * Returns whether the device has any active warnings.
 */
export const hasActiveWarnings = (self: Device): boolean => {
    if (!self.warnings || self.warnings.length === 0) {
        return false;
    }
    return true;
};

/**
 * Returns the device type as a string, or "unknown" if unset.
 */
export const getDeviceTypeString = (self: Device): string => self.DeviceType || "unknown";

/**
 * Returns the effective LoRa network settings for the device, resolving any
 * inheritance from the assigned LoRa network profile.
 */
export const getLoraNetworkSettings = (self: Device): LoraNetworkProfile | undefined =>
    LoraNetworkProfileConfig.getters.getEffectiveSettings(self.loraNetworkProfileConfig);

/**
 * Version selector string that identifies the minimum Gen 4 firmware with Class B support.
 * Format: `<generation>.<type>.<major>.<minor>.<patch+>` — wildcards are `*`.
 */
const CLASS_B_BASIC_SUPPORT_VERSION_SELECTOR = "4.block.*.*.14+";

/** Firmware that accepts a remotely set manual override: all of Gen 4, sprout from the revision that added the command. */
const REMOTE_MANUAL_OVERRIDE_VERSION_SELECTOR = "(4+.*.*.*.*) (3.micro.*.*.1+) (3.block.*.*.3+)";

/**
 * Whether the device's firmware accepts a remote manual override (open, closed, or back to schedule).
 *
 * A device that has never reported its version reads as unsupported: the version arrives with the
 * first uplink, so an absent one means we have no basis to claim the command will be honored.
 */
export const doesDeviceSupportRemoteManualOverride = (self: Pick<Device, "version">): boolean =>
    Boolean(self.version) &&
    DeviceVersion.utilities.doesDeviceVersionMatchVersionSelector(
        self.version,
        REMOTE_MANUAL_OVERRIDE_VERSION_SELECTOR,
    );

/**
 * Determines the device's Class B LoRa support status by checking a series of prerequisites.
 *
 * Class B is a LoRa operating mode that allows the network to schedule downlinks at
 * predictable beacon-aligned windows, enabling lower-latency commands compared to Class A.
 *
 * Checks are performed in order; the first failing check determines the returned status:
 * 1. Device version is known → otherwise `UNKNOWN`
 * 2. Version matches the Class B support selector (`4.block.*.*.14+`) → otherwise `KNOWN_UNSUPPORTED`
 * 3. Effective LoRa network settings are present → otherwise `SUPPORTED_PENDING_CONFIG`
 * 4. Active LoRa mode is `CLASS_B` → otherwise `SUPPORTED_PENDING_CONFIG`
 * 5. A LoRa application ID is assigned → otherwise `SUPPORTED_PENDING_HANDSHAKE`
 *
 * @param self - The device document to evaluate.
 * @returns A {@link ClassBSupportStatus} reflecting how far the device has progressed through Class B setup.
 */
export const getDeviceSupportForClassB = (
    self: Pick<Device, "version" | "loraApplicationId" | "loraNetworkProfileConfig">,
): ClassBSupportStatus => {
    if (!self.version) {
        return ClassBSupportStatus.UNKNOWN;
    }
    if (
        !DeviceVersion.utilities.doesDeviceVersionMatchVersionSelector(
            self.version,
            CLASS_B_BASIC_SUPPORT_VERSION_SELECTOR,
        )
    ) {
        return ClassBSupportStatus.KNOWN_UNSUPPORTED;
    }
    const effectiveNetworkSettings = LoraNetworkProfileConfig.getters.getEffectiveSettings(
        self.loraNetworkProfileConfig,
    );

    if (!effectiveNetworkSettings || effectiveNetworkSettings.responsivenessSettings.loraMode !== LORA_MODE.CLASS_B) {
        return ClassBSupportStatus.SUPPORTED_PENDING_CONFIG;
    }
    if (!self.loraApplicationId) {
        return ClassBSupportStatus.SUPPORTED_PENDING_HANDSHAKE;
    }
    return ClassBSupportStatus.SUPPORTED_AND_AVAILABLE;
};

/**
 * The start time, end time, and active status of a device network test.
 *
 * When no network test has ever been run, `lastNetworkTestStart` and
 * `lastNetworkTestEnd` are `null` and `currentlyConductingNetworkTest` is `false`.
 */
export type NetworkTestPeriod = {
    /** True if a network test is currently in progress at the reference time. */
    currentlyConductingNetworkTest: boolean;
    /** When the most recent network test started, or `null` if none has been run. */
    lastNetworkTestStart: Date | null;
    /** When the most recent network test is/was expected to end, or `null` if none has been run. */
    lastNetworkTestEnd: Date | null;
};

const NULL_NETWORK_TEST_PERIOD: NetworkTestPeriod = {
    currentlyConductingNetworkTest: false,
    lastNetworkTestStart: null,
    lastNetworkTestEnd: null,
};

const computeNetworkTestPeriod = (
    rawStartDate: Date | string | null | undefined,
    now: number,
    durationMs: number = NETWORK_TEST_DURATION_MS,
): NetworkTestPeriod => {
    if (rawStartDate == null) return NULL_NETWORK_TEST_PERIOD;
    const start = new Date(rawStartDate.valueOf());
    if (Number.isNaN(start.valueOf())) return NULL_NETWORK_TEST_PERIOD;
    const startTimestamp = start.valueOf();
    const end = new Date(startTimestamp + durationMs);
    return {
        currentlyConductingNetworkTest: now >= startTimestamp && now < end.valueOf(),
        lastNetworkTestStart: start,
        lastNetworkTestEnd: end,
    };
};

/**
 * Returns the network test period for a first-party device (Sprout or Gen4).
 *
 * Gen 4 block v14+ devices use a 10-minute window instead of 4 hours; see
 * `GEN4_V14_PLUS_NETWORK_TEST_VERSION_SELECTOR`. Only Sprout and Gen4 generations support
 * network tests — all others return `currentlyConductingNetworkTest: false`.
 */
export const getNetworkTestPeriod = (
    self: Pick<Device, "DeviceType" | "sproutNetworkInfo" | "gen4NetworkInfo" | "version">,
): NetworkTestPeriod => {
    const deviceConfig = DeviceTypeConfigurations[self.DeviceType as FirstPartyDeviceType];
    if (!deviceConfig) return NULL_NETWORK_TEST_PERIOD;

    const { generation } = deviceConfig;
    const now = TimeOverride.valueOf();

    if (generation === "sprout" && self.sproutNetworkInfo) {
        return computeNetworkTestPeriod(self.sproutNetworkInfo.lastNetworkTestStartDate, now);
    }
    if (generation === "gen4" && self.gen4NetworkInfo) {
        const isGen4V14Plus =
            self.version != null &&
            DeviceVersion.utilities.doesDeviceVersionMatchVersionSelector(
                self.version,
                GEN4_V14_PLUS_NETWORK_TEST_VERSION_SELECTOR,
            );
        const durationMs = isGen4V14Plus ? GEN4_V14_PLUS_NETWORK_TEST_DURATION_MS : NETWORK_TEST_DURATION_MS;
        return computeNetworkTestPeriod(self.gen4NetworkInfo.lastNetworkTestStartDate, now, durationMs);
    }

    return NULL_NETWORK_TEST_PERIOD;
};

/**
 * Returns the network test duration in milliseconds for a device.
 * Accounts for firmware version: Gen 4 v14+ uses 10 minutes, all others use 4 hours.
 * Returns `null` when the device type is unknown.
 */
export const getNetworkTestDurationMs = (
    self: Pick<Device, "DeviceType" | "sproutNetworkInfo" | "gen4NetworkInfo" | "version">,
): number | null => {
    const deviceConfig = DeviceTypeConfigurations[self.DeviceType as FirstPartyDeviceType];
    if (!deviceConfig) return null;

    const { generation } = deviceConfig;
    if (generation === "sprout") return NETWORK_TEST_DURATION_MS;
    if (generation === "gen4") {
        const isGen4V14Plus =
            self.version != null &&
            DeviceVersion.utilities.doesDeviceVersionMatchVersionSelector(
                self.version,
                GEN4_V14_PLUS_NETWORK_TEST_VERSION_SELECTOR,
            );
        return isGen4V14Plus ? GEN4_V14_PLUS_NETWORK_TEST_DURATION_MS : NETWORK_TEST_DURATION_MS;
    }
    return null;
};

/**
 * Inverse of `SensorCluster.getters.legacyConnectedSensor`: rebuilds the SensorCluster
 * documents that correspond to this device's legacy `connectedSensors` array.
 *
 * For each `SensorSpecifier`, finds the cluster config whose
 * `migrationProperties.legacySensorType` matches the legacy `sensorType` and uses
 * `legacyParametersToClusterParameters` to translate the parameters.
 *
 * Mapping rules for legacy sensors:
 * - `address.portId`: `S{sensorIndex + 1}` (sensor index 0 → "S1", 1 → "S2", 2 → "S3")
 * - `address.channel`: always 0 (legacy sensors are DIRECT-mode)
 * - `address.hostReference`: foreign lookup to this device by `node_id`
 * - `origin`: `userConfigured`, since these clusters mirror user wiring
 *
 * Clusters for the device type's fixed ports are appended after the legacy ones with
 * `origin: fixedDevicePort`.
 *
 * Zones: a sensor's `connectedZones` may be the legacy placeholder `"default"`, meaning "the zones of the
 * device it is plugged into". Stream fragments only understand real zone ids, so it is expanded here against
 * the device's current zones, as legacy ingestion does per point. Clusters never carry `"default"`; the stored
 * specifier does, until it is retired separately.
 *
 * SCD bookkeeping fields (`recordId`, `versionId`, dates, etc.) are not set here —
 * the caller is expected to feed the result through the SCD insert pipeline.
 *
 * Specifiers whose `sensorType` has no matching cluster config (no
 * `migrationProperties.legacySensorType` match) are silently skipped.
 */
export const getConnectedSensorClusters = (self: Device): SensorCluster[] => {
    if (!self.owner) return [];

    const hostReference = new ForeignLookup({
        targetId: self.node_id,
        targetForeignField: "node_id",
        targetCollection: DEVICE_COLLECTION_NAME,
    });
    const owner = self.owner;
    const hostPosition =
        self.latitude != null && self.longitude != null
            ? new VertexInfo({ lat: self.latitude, lng: self.longitude })
            : undefined;

    // Open sensor ports (S1, S2, ...): user-configured via the legacy `connectedSensors` array.
    const openClusters = (self.connectedSensors ?? []).flatMap((specifier, index) => {
        if (!specifier) return [];

        // A sensor type with no cluster config produces no cluster, and therefore no element, no stream
        // reservation, and no recorded data. Silent here by necessity — this getter is pure and runs on
        // hot paths including every reconcile — so the fleet-wide count is reported by the periodic
        // reconcile script instead. See findSensorClusterConfigKeyForLegacySensorType.
        const sensorClusterConfigKey = findSensorClusterConfigKeyForLegacySensorType(specifier.sensorType);
        if (!sensorClusterConfigKey) return [];

        const config = sensorClusterConfigs[sensorClusterConfigKey];
        const parameters = config.migrationProperties!.legacyParametersToClusterParameters(specifier.sensorParameters);

        const cluster = new SensorCluster();
        Object.assign(cluster, {
            address: new SensorClusterAddress({
                hostReference: new ForeignLookup({ ...hostReference }),
                portId: `S${index + 1}`,
                communicationMode: config.communicationMode,
                channel: 0,
            }),
            sensorClusterConfigKey: sensorClusterConfigKey as SensorClusterConfigKey,
            origin: "userConfigured" satisfies SensorClusterOrigin,
            parameters,
            owner,
            connectedZones: getSensorsConnectedZoneIDs(self, specifier),
            acceptableValueRange: specifier.acceptableValueRange,
            acceptableRangeByValveState: specifier.acceptableRangeByValveState,
            acceptableRampUpTimeMs: specifier.acceptableRampUpTimeMs,
            acceptableRampDownTimeMs: specifier.acceptableRampDownTimeMs ?? 0,
            useForVerification: specifier.useForVerification,
            lastReading: specifier.lastReading,
            uplinkPeriod: specifier.uplinkPeriod,
            readingPeriod: specifier.readingPeriod,
            position: specifier.position,
            hostPosition,
        });
        return [cluster];
    });

    // Fixed sensor ports (I1, V1, D1, ...): defined by the device type configuration, not user
    // wiring. The cluster config is determined by `sensorClusterToPopulateWith` on the port
    // definition. Synthesized in memory; persisted/reconciled the same way as open clusters.
    // Gate on isVerdiDevice: third-party fixed clusters are synthesized by the
    // ThirdPartyDevice getters, and these clusters are persisted downstream.
    const deviceConfig = DeviceTypeConfigurations[self.DeviceType as FirstPartyDeviceType];
    const portConfiguration = deviceConfig?.isVerdiDevice ? deviceConfig.sensorPortConfiguration : undefined;
    const fixedClusters = Object.entries(portConfiguration ?? {}).flatMap(([portId, rawPortConfig]) => {
        const portConfig = rawPortConfig as FixedSensorPort | OpenSensorPort | undefined;
        if (!portConfig?.isFixed || !portConfig.sensorClusterToPopulateWith) return [];
        const configKey = portConfig.sensorClusterToPopulateWith as SensorClusterConfigKey;
        const clusterConfig = sensorClusterConfigs[configKey];
        if (!clusterConfig) return [];
        const cluster = new SensorCluster();
        Object.assign(cluster, {
            address: new SensorClusterAddress({
                hostReference: new ForeignLookup({ ...hostReference }),
                portId,
                communicationMode: clusterConfig.communicationMode,
                channel: 0,
            }),
            sensorClusterConfigKey: configKey,
            origin: "fixedDevicePort" satisfies SensorClusterOrigin,
            parameters: {},
            owner,
            connectedZones: getFixedPortConnectedZoneIDs(self, portId),
            hostPosition,
        });
        return [cluster];
    });

    return [...openClusters, ...fixedClusters];
};

/**
 * Builds the warning context object used when posting or clearing warnings for this device.
 *
 * `foreignLookup` identifies the device document in the warnings collection by `node_id`.
 * `targetInfo` carries human-readable metadata shown in warning notifications.
 * `owner` is used to scope warnings to the correct user account.
 */
export const getWarningContext = (self: Device) => ({
    foreignLookup: new ForeignLookup({
        targetId: self.node_id,
        targetForeignField: "node_id",
        targetCollection: DEVICE_COLLECTION_NAME,
    }),
    targetInfo: {
        name: self.name ?? self.node_id,
        node_id: self.node_id,
        _id: self._id,
    },
    owner: self.owner,
    aoi: self._aoi,
});
